import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, Building2, MapPin, Globe, Package, Shield, 
  CheckCircle, XCircle, Clock, Phone, FileText, Ship,
  Mail, Calendar, Loader2, BadgeCheck, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { VerificationStatus, FullyVerifiedBadge, PhoneVerifiedBadge, GSTVerifiedBadge, IECVerifiedBadge, PartialVerificationBadge } from '@/components/VerificationBadges';
import { getCountryFlag } from '@/utils/countryFlags';

const API = `${window.location.origin}/api`;

export default function SellerProfile() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSellerProfile();
  }, [sellerId]);

  const fetchSellerProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch seller details
      const sellerRes = await axios.get(`${API}/seller/${sellerId}/profile`, { headers });
      setSeller(sellerRes.data.seller);
      setProducts(sellerRes.data.products || []);
    } catch (error) {
      console.error('Error fetching seller profile:', error);
      toast.error('Failed to load seller profile');
    } finally {
      setLoading(false);
    }
  };

  // Currency symbol helper
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'AED': 'د.إ',
    };
    return symbols[currency] || currency + ' ';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!seller) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Seller Not Found</h2>
              <p className="text-muted-foreground mb-4">The seller profile you're looking for doesn't exist.</p>
              <Button onClick={() => navigate('/marketplace')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Marketplace
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const isFullyVerified = seller.phone_verified && seller.gstin_verified && seller.iec_status === 'approved';

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="seller-profile-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Seller Header */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Seller Logo/Avatar */}
              <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                <AvatarImage src={seller.company_logo} alt={seller.company_name} />
                <AvatarFallback className="text-2xl bg-primary text-white">
                  {seller.company_name?.charAt(0)?.toUpperCase() || 'S'}
                </AvatarFallback>
              </Avatar>

              {/* Seller Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{seller.company_name}</h1>
                  <FullyVerifiedBadge 
                    phoneVerified={seller.phone_verified}
                    gstinVerified={seller.gstin_verified}
                    iecStatus={seller.iec_status}
                    size="default"
                  />
                </div>
                
                <div className="flex flex-wrap items-center gap-3 text-muted-foreground mb-4">
                  {seller.district && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {seller.district}
                    </span>
                  )}
                  {seller.country && (
                    <span className="flex items-center gap-1">
                      <span className="text-lg">{getCountryFlag(seller.country)}</span>
                      {seller.country}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Member since {new Date(seller.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>

                {/* Unique Verification Badges */}
                <div className="flex flex-wrap items-center gap-2">
                  <PhoneVerifiedBadge verified={seller.phone_verified} size="default" showLabel />
                  <GSTVerifiedBadge verified={seller.gstin_verified} size="default" showLabel />
                  <IECVerifiedBadge status={seller.iec_status} size="default" showLabel />
                  <PartialVerificationBadge 
                    phoneVerified={seller.phone_verified}
                    gstinVerified={seller.gstin_verified}
                    iecStatus={seller.iec_status}
                    size="default"
                  />
                </div>
              </div>

              {/* Contact Button */}
              <div className="flex flex-col gap-2">
                <Button onClick={() => navigate(`/inquiries?seller=${sellerId}`)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Seller
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About the Company Section */}
        {(seller.about || seller.nature_of_business || seller.annual_turnover || seller.business_address) && (
          <Card className="mb-6" data-testid="about-company-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                About the Company
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Company Description */}
              {seller.about && (
                <div>
                  <p className="text-muted-foreground leading-relaxed">{seller.about}</p>
                </div>
              )}

              {/* Company Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                {seller.nature_of_business && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Nature of Business</p>
                    <p className="font-medium">{seller.nature_of_business}</p>
                  </div>
                )}
                {seller.annual_turnover && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Annual Turnover</p>
                    <p className="font-medium">{seller.annual_turnover}</p>
                  </div>
                )}
                {seller.business_address && (
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-muted-foreground">Business Address</p>
                    <p className="font-medium flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      {seller.business_address}
                    </p>
                  </div>
                )}
                {seller.website && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Website</p>
                    <a 
                      href={seller.website.startsWith('http') ? seller.website : `https://${seller.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary hover:underline flex items-center gap-1"
                    >
                      <Globe className="h-4 w-4" />
                      {seller.website}
                    </a>
                  </div>
                )}
              </div>

              {/* Payment Terms Section */}
              {seller.preferred_payment_terms && seller.preferred_payment_terms.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Accepted Payment Terms</h4>
                  <div className="flex flex-wrap gap-2">
                    {seller.preferred_payment_terms.map((term, idx) => (
                      <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {term.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                  {seller.payment_terms_negotiable && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Payment terms are negotiable
                    </p>
                  )}
                  {seller.custom_payment_terms && (
                    <p className="text-sm text-gray-600 mt-2">
                      <span className="font-medium">Note:</span> {seller.custom_payment_terms}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Products Section */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Package className="h-5 w-5" />
            Products by {seller.company_name}
            <Badge variant="secondary">{products.length}</Badge>
          </h2>
        </div>

        {products.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">This seller hasn't listed any products yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <Card 
                key={product.id} 
                className="overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                onClick={() => navigate(`/product/${product.id}`)}
                data-testid={`product-card-${product.id}`}
              >
                <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={product.images[0]} 
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="h-12 w-12 text-slate-400" />
                  )}
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base line-clamp-1">{product.title}</CardTitle>
                  <CardDescription className="line-clamp-1">{product.category}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-primary">
                      {getCurrencySymbol(product.currency || 'INR')}{product.price?.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground">per {product.unit || 'unit'}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-3"
                    onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
